import fetchWrapper from "../lib/apiCall";
import { apiUrl } from "../environmentConfig";

const api = {
  project: {
    getProjects: async (params = {}) => {
      try {
        const { _all = false, ...queryParams } = params;

        if (!_all) {
          const queryString = new URLSearchParams(queryParams).toString();
          const endpoint = `/projects${queryString ? `?${queryString}` : ""}`;
          const response = await fetchWrapper.apiCall(endpoint, "GET");
          return response.results || [];
        }

        const perPage = Number(queryParams.per_page) || 100;
        const allProjects = [];
        let page = 1;
        let totalPages = 1;

        while (page <= totalPages) {
          const queryString = new URLSearchParams({
            ...queryParams,
            page,
            per_page: perPage,
          }).toString();
          const endpoint = `/projects${queryString ? `?${queryString}` : ""}`;
          const response = await fetchWrapper.apiCall(endpoint, "GET");
          allProjects.push(...(response.results || []));
          totalPages = response?.pagination?.pages || 1;
          page += 1;
        }

        return allProjects;
      } catch (error) {
        console.error("Error fetching projects:", error);
        throw error;
      }
    },
    getProjectById: async (projectId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/project/${projectId}`,
          "GET"
        );
        return response.results;
      } catch (error) {
        console.error("Error fetching project:", error);
        throw error;
      }
    },
    createProject: async (data) => {
      try {
        const response = await fetchWrapper.apiCall("/project", "POST", data);
        return response.results;
      } catch (error) {
        console.error("Error creating project:", error);
        throw error;
      }
    },
    updateProject: async (projectId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/project/${projectId}`,
          "PUT",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error updating project:", error);
        throw error;
      }
    },
    deleteProject: async (projectId) => {
      try {
        await fetchWrapper.apiCall(`/project/delete/${projectId}`, "DELETE");
      } catch (error) {
        console.error("Error deleting project:", error);
        throw error;
      }
    },
    toggleTag: async (projectId, tagId) => {
      try {
        const response = await fetchWrapper.apiCall(`/project/tag`, "POST", {
          project_id: projectId,
          tag_id: tagId,
        });
        return response.results;
      } catch (error) {
        console.error("Error toggling project tag:", error);
        throw error;
      }
    },
    toggleFile: async (projectId, fileId) => {
      try {
        const response = await fetchWrapper.apiCall(`/project/file`, "POST", {
          project_id: projectId,
          file_id: fileId,
        });
        return response.results;
      } catch (error) {
        console.error("Error toggling project file:", error);
        throw error;
      }
    },
  },
  task: {
    getTasks: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/tasks${queryString ? `?${queryString}` : ""}`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching tasks:", error);
        throw error;
      }
    },
    getTaskById: async (taskId) => {
      try {
        const response = await fetchWrapper.apiCall(`/task/${taskId}`, "GET");
        return response.results;
      } catch (error) {
        console.error("Error fetching task:", error);
        throw error;
      }
    },
    createTask: async (data) => {
      try {
        const response = await fetchWrapper.apiCall("/task", "POST", data);
        return response.results;
      } catch (error) {
        console.error("Error creating task:", error);
        throw error;
      }
    },
    mergeTasks: async (data) => {
      try {
        const response = await fetchWrapper.apiCall(
          "/task/merge",
          "POST",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error merging task:", error);
        throw error;
      }
    },
    updateTask: async (taskId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/task/${taskId}/update`,
          "PATCH",
          data
        );
        return response.results;
      } catch (error) {
        throw error;
      }
    },
    reorderBacklog: async ({ priority, task_ids }) => {
      try {
        const response = await fetchWrapper.apiCall(
          "/tasks/reorder-backlog",
          "POST",
          {
            priority,
            task_ids,
          }
        );
        return response;
      } catch (error) {
        console.error("Error reordering backlog:", error);
        throw error;
      }
    },
    deleteTask: async (taskId) => {
      try {
        await fetchWrapper.apiCall(`/task/delete/${taskId}`, "DELETE");
      } catch (error) {
        console.error("Error deleting task:", error);
        throw error;
      }
    },
    getSubtasks: async (taskId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/task/${taskId}/subtasks`,
          "GET"
        );
        return response.results || [];
      } catch (error) {
        console.error("Error fetching subtasks:", error);
        throw error;
      }
    },
    assignUsers: async (taskId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/task/${taskId}/assign`,
          "PUT",
          data
        );
        return response.results;
      } catch (error) {
        throw error;
      }
    },
    updateTags: async (taskId, tagIds) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/task/${taskId}/tags`,
          "PUT",
          { tag_ids: tagIds }
        );
        return response.results;
      } catch (error) {
        console.error("Error updating task tags:", error);
        throw error;
      }
    },
    toggleTag: async (taskId, tagId) => {
      try {
        const response = await fetchWrapper.apiCall(`/task/tag`, "POST", {
          task_id: taskId,
          tag_id: tagId,
        });
        return response.results;
      } catch (error) {
        throw error;
      }
    },
    updateTaskDependencies: async (
      taskId,
      { dependency_task_ids, dependant_task_ids }
    ) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/task/${taskId}/dependencies`,
          "PUT",
          { dependency_task_ids, dependant_task_ids }
        );
        return response.results;
      } catch (error) {
        throw error;
      }
    },
    toggleFile: async (taskId, fileId) => {
      try {
        const response = await fetchWrapper.apiCall(`/task/file`, "POST", {
          task_id: taskId,
          file_id: fileId,
        });
        return response.results;
      } catch (error) {
        console.error("Error toggling task file:", error);
        throw error;
      }
    },
  },
  taskComment: {
    getCommentsByTask: async (taskId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/comments/task/${taskId}`,
          "GET"
        );
        return response.results || [];
      } catch (error) {
        console.error("Error fetching task comments:", error);
        throw error;
      }
    },
    createComment: async (data) => {
      try {
        const response = await fetchWrapper.apiCall("/comment", "POST", data);
        return response.results;
      } catch (error) {
        console.error("Error creating task comment:", error);
        throw error;
      }
    },
    updateComment: async (commentId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/comment/${commentId}`,
          "PUT",
          data
        );
        return response;
      } catch (error) {
        console.error("Error updating task comment:", error);
        throw error;
      }
    },
    deleteComment: async (commentId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/comment/delete/${commentId}`,
          "DELETE"
        );
        return response;
      } catch (error) {
        console.error("Error deleting task comment:", error);
        throw error;
      }
    },
  },
  activityLog: {
    getByTask: async (taskId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/activity-logs/task/${taskId}`,
          "GET"
        );
        return response.results || [];
      } catch (error) {
        console.error("Error fetching activity logs:", error);
        throw error;
      }
    },
    getByProject: async (projectId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/activity-logs/project/${projectId}`,
          "GET"
        );
        return response.results || [];
      } catch (error) {
        console.error("Error fetching activity logs:", error);
        throw error;
      }
    },
    getBySprint: async (sprintId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/activity-logs/sprint/${sprintId}`,
          "GET"
        );
        return response.results || [];
      } catch (error) {
        console.error("Error fetching activity logs:", error);
        throw error;
      }
    },
    updateComment: async (commentId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/comment/${commentId}`,
          "PUT",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error updating task comment:", error);
        throw error;
      }
    },
    deleteComment: async (commentId) => {
      try {
        await fetchWrapper.apiCall(`/comment/delete/${commentId}`, "DELETE");
      } catch (error) {
        console.error("Error deleting task comment:", error);
        throw error;
      }
    },
  },
  company: {
    getCompanies: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpointBase = params?.q ? "/companies/search" : "/companies";
        const endpoint = `${endpointBase}${
          queryString ? `?${queryString}` : ""
        }`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching companies:", error);
        throw error;
      }
    },
    getCompanyById: async (companyId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/company/${companyId}`,
          "GET"
        );
        return response.results;
      } catch (error) {
        console.error("Error fetching company:", error);
        throw error;
      }
    },
    createCompany: async (data) => {
      try {
        const response = await fetchWrapper.apiCall("/company", "POST", data);
        return response.results;
      } catch (error) {
        if (error?.status !== 409) {
          console.error("Error creating company:", error);
        }
        throw error;
      }
    },
    updateCompany: async (companyId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/company/${companyId}`,
          "PUT",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error updating company:", error);
        throw error;
      }
    },
  },
  contact: {
    getContacts: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/contacts${queryString ? `?${queryString}` : ""}`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching contacts:", error);
        throw error;
      }
    },
    getContactById: async (contactId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/contact/${contactId}`,
          "GET"
        );
        return response.results;
      } catch (error) {
        console.error("Error fetching contact:", error);
        throw error;
      }
    },
    createContact: async (data) => {
      try {
        const response = await fetchWrapper.apiCall("/contact", "POST", data);
        return response.results;
      } catch (error) {
        console.error("Error creating contact:", error);
        throw error;
      }
    },
    updateContact: async (contactId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/contact/${contactId}`,
          "PUT",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error updating contact:", error);
        throw error;
      }
    },
  },
  sprint: {
    getSprints: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/sprints${queryString ? `?${queryString}` : ""}`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching sprints:", error);
        throw error;
      }
    },
    getSprintById: async (sprintId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/sprint/${sprintId}`,
          "GET"
        );
        return response.results;
      } catch (error) {
        console.error("Error fetching sprint:", error);
        throw error;
      }
    },
    getActiveSprints: async (params = {}) => {
      try {
        const response = await fetchWrapper.apiCall(`/sprint/active`, "GET");
        return response.results;
      } catch (error) {
        console.error("Error fetching active sprint:", error);
        throw error;
      }
    },
    getInActiveSprints: async (params = {}) => {
      try {
        const response = await fetchWrapper.apiCall(`/sprint/inactive`, "GET");
        return response.results;
      } catch (error) {
        console.error("Error fetching active sprint:", error);
        throw error;
      }
    },

    createSprint: async (data) => {
      try {
        const response = await fetchWrapper.apiCall("/sprint", "POST", data);
        return response.results;
      } catch (error) {
        console.error("Error creating sprint:", error);
        throw error;
      }
    },
    updateSprint: async (sprintId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/sprint/${sprintId}`,
          "PUT",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error updating sprint:", error);
        throw error;
      }
    },
    getProjectsBySprint: async (sprintId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/sprint/${sprintId}/projects`,
          "GET"
        );
        return response.results;
      } catch (error) {
        console.error("Error fetching sprint:", error);
        throw error;
      }
    },
    deleteSprint: async (sprintId) => {
      try {
        await fetchWrapper.apiCall(`/sprint/${sprintId}`, "DELETE");
      } catch (error) {
        console.error("Error deleting sprint:", error);
        throw error;
      }
    },
    assignTasks: async (sprintId, taskIds) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/sprint/${sprintId}/tasks`,
          "PUT",
          { task_ids: taskIds }
        );
        return response.results;
      } catch (error) {
        console.error("Error assigning tasks to sprint:", error);
        throw error;
      }
    },
    removeTasksFromSprint: async (sprintId, taskIds) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/sprint/${sprintId}/tasks/remove`,
          "POST",
          { task_ids: taskIds }
        );
        return response.results;
      } catch (error) {
        console.error("Error removing tasks from sprint:", error);
        throw error;
      }
    },
  },
  projectStatus: {
    getProjectStatuses: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/project-statuses${
          queryString ? `?${queryString}` : ""
        }`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching project statuses:", error);
        throw error;
      }
    },
  },
  user: {
    getUsers: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/users${queryString ? `?${queryString}` : ""}`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching users:", error);
        throw error;
      }
    },
    getUsersByOrganization: async (orgId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/users/organization/${orgId}`,
          "GET"
        );
        return response.results || [];
      } catch (error) {
        console.error("Error fetching users by organization:", error);
        throw error;
      }
    },
    getUserById: async (userId) => {
      try {
        const response = await fetchWrapper.apiCall(`/user/${userId}`, "GET");
        return response.results;
      } catch (error) {
        console.error("Error fetching user:", error);
        throw error;
      }
    },
    searchUsers: async (query) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/users/search?q=${query}`,
          "GET"
        );
        return response.results || [];
      } catch (error) {
        console.error("Error searching users:", error);
        throw error;
      }
    },
  },
  tag: {
    getTags: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/tags${queryString ? `?${queryString}` : ""}`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching tags:", error);
        throw error;
      }
    },
    getTagById: async (tagId) => {
      try {
        const response = await fetchWrapper.apiCall(`/tag/${tagId}`, "GET");
        return response.results;
      } catch (error) {
        console.error("Error fetching tag:", error);
        throw error;
      }
    },
    createTag: async (data) => {
      try {
        const response = await fetchWrapper.apiCall("/tag", "POST", data);
        return response.results;
      } catch (error) {
        console.error("Error creating tag:", error);
        throw error;
      }
    },
    updateTag: async (tagId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/tag/${tagId}`,
          "PUT",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error updating tag:", error);
        throw error;
      }
    },
    deleteTag: async (tagId) => {
      try {
        await fetchWrapper.apiCall(`/tag/delete/${tagId}`, "DELETE");
      } catch (error) {
        console.error("Error deleting tag:", error);
        throw error;
      }
    },
  },
  taskStatus: {
    getTaskStatuses: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/task-statuses${
          queryString ? `?${queryString}` : ""
        }`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching task statuses:", error);
        throw error;
      }
    },
    getTaskStatusById: async (statusId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/task-status/${statusId}`,
          "GET"
        );
        return response.results;
      } catch (error) {
        console.error("Error fetching task status:", error);
        throw error;
      }
    },
    createTaskStatus: async (data) => {
      try {
        const response = await fetchWrapper.apiCall(
          "/task-status",
          "POST",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error creating task status:", error);
        throw error;
      }
    },
    updateTaskStatus: async (statusId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/task-status/${statusId}`,
          "PUT",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error updating task status:", error);
        throw error;
      }
    },
    deleteTaskStatus: async (statusId) => {
      try {
        await fetchWrapper.apiCall(`/task-status/delete/${statusId}`, "DELETE");
      } catch (error) {
        console.error("Error deleting task status:", error);
        throw error;
      }
    },
  },
  contract: {
    getContracts: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/contracts${queryString ? `?${queryString}` : ""}`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching contracts:", error);
        throw error;
      }
    },
    getContractById: async (contractId) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/contract/${contractId}`,
          "GET"
        );
        return response.results;
      } catch (error) {
        console.error("Error fetching contract:", error);
        throw error;
      }
    },
    createContract: async (data) => {
      try {
        const response = await fetchWrapper.apiCall("/contract", "POST", data);
        return response.results;
      } catch (error) {
        console.error("Error creating contract:", error);
        throw error;
      }
    },
    updateContract: async (contractId, data) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/contract/${contractId}`,
          "PUT",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error updating contract:", error);
        throw error;
      }
    },
    searchContracts: async (query) => {
      try {
        const response = await fetchWrapper.apiCall(
          `/contracts/search?q=${query}`,
          "GET"
        );
        return response.results || [];
      } catch (error) {
        console.error("Error searching contracts:", error);
        throw error;
      }
    },
    toggleFile: async (contractId, fileId) => {
      try {
        const response = await fetchWrapper.apiCall(`/contract/file`, "POST", {
          contract_id: contractId,
          file_id: fileId,
        });
        return response.results;
      } catch (error) {
        console.error("Error toggling contract file:", error);
        throw error;
      }
    },
  },
  file: {
    createFiles: async (formData) => {
      const response = await fetch(`${apiUrl}/file`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!response.ok) {
        const error = new Error("File upload failed");
        error.status = response.status;
        throw error;
      }
      return response.json();
    },
    deleteFile: async (fileId) => {
      try {
        await fetchWrapper.apiCall(`/file/delete/${fileId}`, "DELETE");
      } catch (error) {
        console.error("Error deleting file:", error);
        throw error;
      }
    },
  },
  notification: {
    getNotificationCount: async () => {
      try {
        const response = await fetchWrapper.apiCall(
          `/notifications/count`,
          "GET"
        );
        return response.results;
      } catch (error) {
        console.error("Error fetching notifications count:", error);
        throw error;
      }
    },

    getNotifications: async (signal) => {
      try {
        const response = await fetchWrapper.apiCall(
          "/notifications",
          "GET",
          null,
          { signal }
        );
        return response.results;
      } catch (error) {
        console.error("Error fetching notifications", error);
        throw error;
      }
    },

    clearNotifications: async (data) => {
      try {
        const response = await fetchWrapper.apiCall(
          "/notifications/read",
          "PUT",
          data
        );
        return response.results;
      } catch (error) {
        console.error("Error clear notifications", error);
        throw error;
      }
    },
  },
  org: {
    getOrgs: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpointBase = params?.q
          ? "/organizations/search"
          : "/organizations";
        const endpoint = `${endpointBase}${
          queryString ? `?${queryString}` : ""
        }`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching organizations:", error);
        throw error;
      }
    },
  },
  report: {
    getDeveloperReport: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/reports/developer${
          queryString ? `?${queryString}` : ""
        }`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results;
      } catch (error) {
        console.error("Error fetching developer report:", error);
        throw error;
      }
    },
    getTeamTaskCompletionReport: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/reports/team-task-completion${
          queryString ? `?${queryString}` : ""
        }`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results;
      } catch (error) {
        console.error("Error fetching team task completion report:", error);
        throw error;
      }
    },
    getQaReport: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/reports/qa${queryString ? `?${queryString}` : ""}`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results;
      } catch (error) {
        console.error("Error fetching QA report:", error);
        throw error;
      }
    },
    getSprintsInRange: async (params = {}) => {
      try {
        const queryString = new URLSearchParams(params).toString();
        const endpoint = `/reports/sprints-in-range${
          queryString ? `?${queryString}` : ""
        }`;
        const response = await fetchWrapper.apiCall(endpoint, "GET");
        return response.results || [];
      } catch (error) {
        console.error("Error fetching sprints in range:", error);
        throw error;
      }
    },
  },
};

export default api;
